/**
 * What the service worker may keep. Pure so it is unit-tested.
 *
 * Security reasoning (see docs/SECURITY.md): only read-only, per-equipment and template data may fall back to a cached
 * copy, because the field flow needs them with no signal. Auth, CSRF, every mutation and every other API read always go
 * to the network. The cache is short-lived, bounded, same-origin only and emptied on sign-out.
 */
export const API_CACHE = { name: 'api-fallback-v1', maxAgeSeconds: 24 * 3600, maxEntries: 60 }

export type Policy = 'network-first' | 'never' | 'precache-or-network'

const CACHEABLE_API = [
  /^\/api\/scan\/[^/]+\/$/, // equipment card behind an NFC/QR tag
  /^\/api\/equipment\/$/, // only with ?code= (checked below)
  /^\/api\/templates\/$/,
  /^\/api\/template-versions\/\d+\/$/,
]

export function cachePolicy(url: URL, method: string, origin = url.origin): Policy {
  if (url.origin !== origin) return 'never'
  if (!url.pathname.startsWith('/api/')) return method === 'GET' ? 'precache-or-network' : 'never'
  if (method !== 'GET') return 'never'
  if (url.pathname.startsWith('/api/auth/')) return 'never'
  if (!CACHEABLE_API.some((re) => re.test(url.pathname))) return 'never'
  if (url.pathname === '/api/equipment/' && !url.searchParams.has('code')) return 'never'
  return 'network-first'
}

export function shouldClearOnMessage(data: unknown): boolean {
  return typeof data === 'object' && data !== null && (data as { type?: unknown }).type === 'clear-api-cache'
}

export const CACHED_HEADER = 'x-cached-copy'

/** Mark a response that came from the fallback cache, so the page can tell the user it shows saved data. */
export function tagCached(response: Response): Response {
  const headers = new Headers(response.headers)
  headers.set(CACHED_HEADER, '1')
  return new Response(response.body, { status: response.status, statusText: response.statusText, headers })
}
